import React, { useState } from 'react';
import {
    TextField,
    Button,
    Box,
    Grid,
    Typography,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    InputAdornment,
    Container,
    Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHeart,
    faUser,
    faEnvelope,
    faHome,
    faPhone,
    faMessage,
    faMapMarkerAlt,
    faCity,
} from '@fortawesome/free-solid-svg-icons';
import { DonationService } from '../../services/donor.service';

const AmountButton = styled(Button)<{ selected?: boolean }>(({ theme, selected }) => ({
    minHeight: '80px',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    position: 'relative',
    borderRadius: theme.spacing(2),
    transition: 'all 0.3s ease',
    background: selected
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : theme.palette.background.paper,
    color: selected ? '#fff' : theme.palette.text.primary,
    border: selected ? 'none' : `2px solid ${theme.palette.divider}`,
    boxShadow: selected ? '0 8px 24px rgba(102, 126, 234, 0.4)' : 'none',
    transform: selected ? 'scale(1.05)' : 'scale(1)',
    '&:hover': {
        transform: 'scale(1.08)',
        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
        background: selected
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : theme.palette.action.hover,
    },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: theme.spacing(3),
    background: 'linear-gradient(145deg, #ffffff 0%, #f5f7fa 100%)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: theme.spacing(2),
    color: '#fff',
}));

const suggestedAmounts = [10, 20, 50, 100, 200, 500];

interface DonorFormProps {
    onSuccess?: () => void;
}

export const DonorForm: React.FC<DonorFormProps> = ({ onSuccess }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        address: '',
        postalCode: '',
        city: '',
        amount: 0,
        phoneNumber: '',
        description: '',
    });

    const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
    const [customAmount, setCustomAmount] = useState<string>('');
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleAmountSelect = (amount: number) => {
        setSelectedAmount(amount);
        setShowCustomInput(false);
        setCustomAmount('');
        setFormData(prev => ({ ...prev, amount }));
        if (errors.amount) {
            setErrors(prev => ({ ...prev, amount: '' }));
        }
    };

    const handleCustomAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setCustomAmount(value);
        const numValue = parseInt(value);
        if (!isNaN(numValue) && numValue >= 5 && numValue <= 500 && numValue % 5 === 0) {
            setSelectedAmount(numValue);
            setFormData(prev => ({ ...prev, amount: numValue }));
            if (errors.amount) {
                setErrors(prev => ({ ...prev, amount: '' }));
            }
        } else {
            setSelectedAmount(null);
        }
    };

    const handleShowCustomInput = () => {
        setShowCustomInput(true);
        setSelectedAmount(null);
        setFormData(prev => ({ ...prev, amount: 0 }));
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'Le prénom est obligatoire';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Le nom est obligatoire';
        }
        if (!formData.email.trim()) {
            newErrors.email = "L'email est obligatoire";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Email invalide';
        }
        if (!formData.address.trim()) {
            newErrors.address = "L'adresse est obligatoire";
        }
        if (!formData.postalCode.trim()) {
            newErrors.postalCode = 'Le code postal est obligatoire';
        } else if (!/^[0-9]{5}$/.test(formData.postalCode)) {
            newErrors.postalCode = 'Le code postal doit contenir 5 chiffres';
        }
        if (!formData.city.trim()) {
            newErrors.city = 'La ville est obligatoire';
        }
        if (!formData.amount || formData.amount < 5) {
            newErrors.amount = 'Le montant minimum est 5€';
        } else if (formData.amount > 500) {
            newErrors.amount = 'Le montant maximum est 500€';
        } else if (formData.amount % 5 !== 0) {
            newErrors.amount = 'Le montant doit être un multiple de 5€';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            setSnackbarSeverity('error');
            setSnackbarMsg('Veuillez corriger les erreurs dans le formulaire');
            setSnackbarOpen(true);
            return;
        }

        setIsSubmitting(true);

        try {
            await DonationService.createDonation(formData);
            setSubmitSuccess(true);
            setSnackbarSeverity('success');
            setSnackbarMsg('Votre don a été enregistré avec succès !');
            setSnackbarOpen(true);

            // Appeler le callback de succès
            if (onSuccess) {
                onSuccess();
            }

            // Reset form
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                address: '',
                postalCode: '',
                city: '',
                amount: 0,
                phoneNumber: '',
                description: '',
            });
            setSelectedAmount(null);
            setCustomAmount('');
            setShowCustomInput(false);
        } catch (error: any) {
            setSnackbarSeverity('error');
            setSnackbarMsg(error.response?.data?.message || 'Une erreur est survenue lors de l\'envoi du don');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNewDonation = () => {
        setSubmitSuccess(false);
    };

    if (submitSuccess) {
        return (
            <Container>
                <StyledPaper elevation={3}>
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <FontAwesomeIcon icon={faHeart} style={{ fontSize: '80px', color: '#667eea', marginBottom: '16px' }} />
                        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
                            Merci pour votre générosité !
                        </Typography>
                        <Typography variant="h6" color="text.secondary" paragraph>
                            Votre don a été enregistré avec succès.
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Vous recevrez prochainement un email de confirmation avec les détails de votre contribution.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleNewDonation}
                            sx={{
                                mt: 3,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                            }}
                        >
                            Faire un autre don
                        </Button>
                    </Box>
                </StyledPaper>
            </Container>
        );
    }

    return (
        <>
            <StyledPaper elevation={3}>
                <HeaderSection>
                    <FontAwesomeIcon icon={faHeart} style={{ fontSize: '40px', marginBottom: '16px' }} />
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Faites un don
                    </Typography>
                    <Typography variant="h6">
                        Votre contribution fait la différence
                    </Typography>
                </HeaderSection>

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    {/* Section Montant */}
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                            1. Choisissez votre montant
                        </Typography>

                        <Grid container spacing={2}>
                            {suggestedAmounts.map((amount) => (
                                <Grid size={{ xs:12, md: 6 }} key={amount}>
                                    <AmountButton
                                        fullWidth
                                        selected={selectedAmount === amount}
                                        onClick={() => handleAmountSelect(amount)}
                                        variant={selectedAmount === amount ? 'contained' : 'outlined'}
                                    >
                                        {amount}€
                                    </AmountButton>
                                </Grid>
                            ))}
                        </Grid>

                        <Box sx={{ mt: 3 }}>
                            {!showCustomInput ? (
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    size="large"
                                    onClick={handleShowCustomInput}
                                    sx={{ py: 2, fontSize: '1.1rem' }}
                                >
                                    Autre montant (multiple de 5€)
                                </Button>
                            ) : (
                                <TextField
                                    fullWidth
                                    label="Montant personnalisé"
                                    type="number"
                                    value={customAmount}
                                    onChange={handleCustomAmountChange}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">€</InputAdornment>,
                                    }}
                                    inputProps={{
                                        step: 5,
                                        min: 5,
                                        max: 500,
                                    }}
                                    helperText="Entre 5€ et 500€ (multiple de 5€)"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            fontSize: '1.2rem',
                                            '& input': {
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                            },
                                        },
                                    }}
                                />
                            )}
                        </Box>

                        {errors.amount && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {errors.amount}
                            </Alert>
                        )}

                        {selectedAmount && (
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <Chip
                                    label={`Montant sélectionné : ${selectedAmount}€`}
                                    color="primary"
                                    size="medium"
                                    sx={{
                                        fontSize: '1.2rem',
                                        py: 3,
                                        px: 2,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Section Informations personnelles */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                            2. Vos informations
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid size={{ xs:12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    name="firstName"
                                    label="Prénom *"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FontAwesomeIcon icon={faUser} style={{ color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs:12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    name="lastName"
                                    label="Nom *"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FontAwesomeIcon icon={faUser} style={{ color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    name="email"
                                    label="Email *"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs:12 }}>
                                <TextField
                                    fullWidth
                                    name="address"
                                    label="Adresse *"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    error={!!errors.address}
                                    helperText={errors.address}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FontAwesomeIcon icon={faHome} style={{ color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs:12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    name="postalCode"
                                    label="Code Postal *"
                                    value={formData.postalCode}
                                    onChange={handleInputChange}
                                    error={!!errors.postalCode}
                                    helperText={errors.postalCode}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs:12, md: 8 }}>
                                <TextField
                                    fullWidth
                                    name="city"
                                    label="Ville *"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    error={!!errors.city}
                                    helperText={errors.city}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FontAwesomeIcon icon={faCity} style={{ color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs:12 }}>
                                <TextField
                                    fullWidth
                                    name="phoneNumber"
                                    label="Téléphone (optionnel)"
                                    value={formData.phoneNumber}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FontAwesomeIcon icon={faPhone} style={{ color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs:12 }}>
                                <TextField
                                    fullWidth
                                    name="description"
                                    label="Message / Raison du don (optionnel)"
                                    multiline
                                    rows={4}
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                                                <FontAwesomeIcon icon={faMessage} style={{ color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Bouton de soumission */}
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={isSubmitting || !selectedAmount}
                            startIcon={
                                isSubmitting ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <FontAwesomeIcon icon={faHeart} />
                                )
                            }
                            sx={{
                                px: 6,
                                py: 2,
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                    boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                                },
                                '&:disabled': {
                                    background: '#ccc',
                                },
                            }}
                        >
                            {isSubmitting ? 'Envoi en cours...' : 'Confirmer mon don'}
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            * Champs obligatoires
                        </Typography>
                    </Box>
                </Box>
            </StyledPaper>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMsg}
                </Alert>
            </Snackbar>
        </>
    );
};